import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { getFilePool } from '../api/data-connectors/filepool'
import { postImatch } from '../api/data-connectors/imatch'
import { getSelectedText, getWorkingDocumentSnapshot } from '../api/word/word-service'
import { WORKING_DOCUMENT_ID } from '../constants/files'
import type { FileInfoType } from '../types'
import { useChatSession } from './useChatSession'
import { useDocumentName } from './useDocumentName'
import { useNotification } from './useNotification'
import { useSelectedContext } from './useSelectedContext'
import { useWorkingDocument } from './useWorkingDocument'

export const IMATCH_MUTATION_KEY = ['imatch'] as const
export const WORKING_DOCUMENT_BACKEND_ID = 'current_document'

export function useImatch() {
  const { notify } = useNotification()
  const { t } = useTranslation()
  const { draftMessage } = useChatSession()
  const { replaceSelections } = useSelectedContext()
  const { isWorkingDocumentExcluded } = useWorkingDocument()
  const { data: documentName } = useDocumentName(!isWorkingDocumentExcluded)
  const queryClient = useQueryClient()

  return useMutation({
    mutationKey: IMATCH_MUTATION_KEY,
    mutationFn: async () => {
      const [workingDocSections, mouseSelection] = await Promise.all([
        isWorkingDocumentExcluded ? undefined : getWorkingDocumentSnapshot(),
        getSelectedText(),
      ])
      const iMatchResult = await postImatch({
        currentDocument: workingDocSections ? { sections: workingDocSections } : undefined,
        editorContent: draftMessage || undefined,
        mouseSelection: mouseSelection ?? undefined,
      })

      // fetchQuery uses the cache when fresh, calls the API when stale or absent.
      const filePool = await queryClient.fetchQuery<FileInfoType[]>({
        queryKey: ['filepool'],
        queryFn: getFilePool,
        staleTime: 600_000,
      })

      const resolvedMatches = iMatchResult.matches.map(({ file_id, sections }) => {
        if (file_id === WORKING_DOCUMENT_BACKEND_ID) {
          return {
            id: WORKING_DOCUMENT_ID,
            fileName: documentName ?? t('files.workingDocumentFileName'),
            extension: 'docx',
            sections,
          }
        }
        const file = filePool.find((f) => f.id === file_id)
        if (!file) return null
        return {
          id: file_id,
          fileName: file.name,
          extension: file.type,
          sections,
        }
      })

      const activeFilePool = filePool.filter((f) => !f.isExcluded)

      return {
        selections: resolvedMatches.filter((m) => m !== null),
        sourceCount: activeFilePool.length + (isWorkingDocumentExcluded ? 0 : 1),
      }
    },
    onSuccess: ({ selections, sourceCount }) => {
      replaceSelections(selections)
      if (selections.length === 0) {
        notify({
          isSuccess: false,
          text: t('chatInput.contextAssistNoMatches'),
          position: 'top',
        })
        return
      }
      const itemCount = selections.reduce((sum, f) => sum + (f.sections.length || 1), 0)
      notify({
        isSuccess: true,
        text: t('chatInput.contextAssistSuccess', { count: itemCount, sourceCount }),
        position: 'top',
      })
    },
    onError: () => {
      notify({ isSuccess: false, text: t('chatInput.contextAssistError'), position: 'top' })
    },
  })
}
